/**
 * Business – Markt
 * /business/markt
 *
 * Kaufbereich: Materialien, Labor-Zutaten, Fahrzeuge, Kontakte.
 * Tagespreise 70–130%, Tageslimits, 3 Lieferanten-Optionen.
 * Fahrzeuge = Transport (PKW/LKW) + Verkauf (Eiswagen etc.)
 */

import { api, ApiError } from '../../services/api.js';
import { gameState } from '../../state/gameState.js';
import { renderPropertyOptions } from '../../utils/property-select.js';
import { showToast } from '../../utils/toast.js';
import { refreshBalance } from '../../utils/refreshBalance.js';
import { tryOfferEmergencyGarage } from '../../components/EmergencyGarageOfferModal.js';
import { renderWhenIdle, cancelPendingRender } from '../../utils/background-render.js';
import { fetchStellplaetze, describeOccupants, type StellplatzOverview } from '../../services/stellplaetze.js';
import { MOBILE_LAYOUT_QUERY } from '../../utils/mobile-layout.js';

const MARKT_RENDER_KEY = 'markt';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface SupplierOption {
  supplier:        string;
  label:           string;
  priceCents:      string;
  deliveryMinutes: number;
  locked:          boolean;
  lockReason:      string | null;
}

interface CatalogItem {
  key:                  string;
  label:                string;
  description:          string;
  category:             'MATERIAL' | 'LABOR_INPUT' | 'LAB_EQUIPMENT' | 'VEHICLE' | 'GARDEN' | 'CONTACT';
  currency:             'NOX' | 'BLACK_MONEY';
  stackMax:             number;
  unitLabel:            string;
  dailyLimit:           number | null;
  limitLabel:           string | null;
  boughtInPeriod:       number;
  remainingInPeriod:    number | null;
  lore:                 string;
  dailyMultiplier:      number;
  priceFixed:           boolean;
  /** NT-VIERTEL-FIX-01 (V1): Fahrzeug braucht einen Stellplatz. */
  requiresStellplatz:   boolean;
  suppliers:            SupplierOption[];
  tier?:                number;
  subgroup?:            string;
  vehicleCapacity?:     number;
  vehicleCondition?:    string;
  vehicleType?:         string;
  contactLevelRequired?: number;
  wantedLevelLock?:     number;
  alreadyPurchased?:    boolean;
}

interface CatalogResponse {
  items: CatalogItem[];
  today: string;
}

interface PendingOrder {
  id:               string;
  itemKey:          string;
  itemLabel:        string;
  quantity:         number;
  pricePaidCents:   string;
  currency:         string;
  supplier:         string;
  targetPropertyId: string;
  orderedAt:        string;
  deliveredAt:      string;
  remainingMs:      number;
}

interface OwnedWarehouse {
  id:         string;
  label:      string;
  customName: string | null;
  nickname:   string | null;
  slots:      number;
  currentLoad: number;
  isHideout?: boolean;
}

interface PropertiesResponse {
  warehouses: OwnedWarehouse[];
  hideouts:   OwnedWarehouse[];
}

// ─── Konstanten ───────────────────────────────────────────────────────────────

// NT-UI-MODAL-LISTE-01: wie lange "AUSGELIEFERT" sichtbar bleibt, bevor der Eintrag
// automatisch aus der Liste ausfadet (L2-Verhalten statt minutenlangem Stehenbleiben).
const DELIVERED_DISPLAY_DURATION_MS = 3500;

// ─── State ────────────────────────────────────────────────────────────────────

let catalog:      CatalogResponse | null = null;
let pendingOrders: PendingOrder[]         = [];
let warehouses:   OwnedWarehouse[]        = [];
// NT-VIERTEL-FIX-01 (V1): Stellplätze für den Fahrzeugkauf (Ziel = Immobilie mit freiem Platz).
let stellplaetze: StellplatzOverview | null = null;
let activeCategory: CatalogItem['category'] = 'MATERIAL';
let buyModal:     { item: CatalogItem; supplier: SupplierOption } | null = null;
let buyQty = 1;
let buyTargetPropertyId = '';
let refreshTimer: ReturnType<typeof setInterval> | null = null;
let countdownTimer: ReturnType<typeof setInterval> | null = null;

// Lokale Grace-Phase für gerade zugestellte Bestellungen (NT-UI-MODAL-LISTE-01).
// Wird gesetzt, sobald der Countdown lokal 0 erreicht — überlebt einen zwischenzeitlichen
// reload(), da die API zugestellte Orders sofort nicht mehr zurückliefert (siehe Schritt-0-Bericht).
const deliveredGrace = new Map<string, { order: PendingOrder; graceStartedAt: number }>();

// ─── Helfer ───────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtCents(cents: string, currency: string): string {
  const val = (parseInt(cents, 10) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === 'NOX' || currency === 'EUR' ? `${val} ₦` : `${val} S-₦`;
}

function fmtDelivery(minutes: number): string {
  if (minutes === 0) return 'SOFORT';
  if (minutes < 60)  return `${String(minutes)} Min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${String(h)}h ${String(m)}m` : `${String(h)}h`;
}

function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'FERTIG';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${String(h)}h ${String(m)}m`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function categoryLabel(cat: string): string {
  return ({
    MATERIAL:      'MATERIALIEN',
    LABOR_INPUT:   'ZUTATEN & VERBRAUCH',
    LAB_EQUIPMENT: 'LABOR-EQUIPMENT',
    VEHICLE:       'FAHRZEUGE',
    GARDEN:        'GARTENCENTER',
    CONTACT:       'KONTAKTE',
  } as Record<string, string>)[cat] ?? cat;
}

// NT-MARKT-TAXONOMIE-01 / NT-MARKT-GARTEN-SUBGROUPS-01 / NT-MARKT-KATEGORIEN-SUBGROUPS-01:
// Anzeige-Label für subgroup-Header innerhalb einer Kategorie. Reihenfolge der Anzeige selbst
// kommt vom Backend (sortMarketItemsForDisplay/SUBGROUP_ORDER, market.definitions.ts) — hier
// nur Labels. LAB_EQUIPMENT und CONTACT haben bewusst keine subgroups (zu klein/homogen).
function subgroupLabel(subgroup: string): string {
  return ({
    // GARDEN
    ZELTE:                 'GROWZELTE',
    TOEPFE:                'TÖPFE & SUBSTRAT',
    BELEUCHTUNG:           'BELEUCHTUNG',
    BEWAESSERUNG:          'BEWÄSSERUNG',
    PFLEGE_VERBRAUCH:      'PFLEGE & MESSTECHNIK',
    TABAK_VERARBEITUNG:    'TABAK-VERARBEITUNG',
    SAMEN_HANF:            'SAMEN: HANF',
    SAMEN_TABAK:           'SAMEN: TABAK',
    // LABOR_INPUT ("ZUTATEN & VERBRAUCH")
    AUSRUESTUNG_VERBRAUCH: 'AUSRÜSTUNG & VERBRAUCH',
    ZUTATEN_LEGAL:         'LEGALE ZUTATEN',
    ZUTATEN_DUAL:          'ZUTATEN (LEGAL + ILLEGAL)',
    ZUTATEN_BASIS:         'BASIS-/ZWISCHENPRODUKTE',
    ZUTATEN_BASIS_ILLEGAL: 'BASIS- & ILLEGALE ZUTATEN',
    ZUTATEN_ILLEGAL:       'ILLEGALE ZUTATEN',
    ROLLING_ZUBEHOER:      'ROLLTISCH-ZUBEHÖR',
    // MATERIAL
    SCHROTT:               'SCHROTT',
    ALLTAGSMATERIAL:       'ALLTAGSMATERIAL',
    INDUSTRIEMATERIAL:     'INDUSTRIEMATERIAL',
    BAUTEILE:              'BAUTEILE',
    WERKSTATT_TEILE:       'WERKSTATT-TEILE',
    // VEHICLE
    ZWEIRAD:               'ZWEIRAD',
    PKW:                   'PKW',
    NUTZFAHRZEUGE:         'NUTZFAHRZEUGE',
  } as Record<string, string>)[subgroup] ?? subgroup;
}

/**
 * Zitat-Zeile (NT-FAHRZEUGE-01): nur anzeigen, wenn ein eigenes Zitat existiert — leer oder
 * identisch mit der Beschreibung (ggf. in Anführungszeichen) → keine Zeile, kein Fallback.
 */
function renderLore(item: CatalogItem, cssClass: string): string {
  const lore = item.lore.trim();
  if (lore === '') return '';
  const unquoted = lore.replace(/^["„“]+|["“”]+$/g, '').trim();
  if (unquoted === item.description.trim()) return '';
  return `<p class="${cssClass}">${esc(lore)}</p>`;
}

function priceMultiplierBadge(mult: number): string {
  const pct = Math.round((mult - 1) * 100);
  if (pct > 5)  return `<span class="markt-mult markt-mult--up">+${String(pct)}%</span>`;
  if (pct < -5) return `<span class="markt-mult markt-mult--down">${String(pct)}%</span>`;
  return `<span class="markt-mult markt-mult--neutral">NORMAL</span>`;
}

// ─── Render: Kategorie-Tabs ───────────────────────────────────────────────────

function renderCategoryTabs(): string {
  const categories: CatalogItem['category'][] = ['MATERIAL', 'LABOR_INPUT', 'LAB_EQUIPMENT', 'VEHICLE', 'GARDEN', 'CONTACT'];
  return `<div class="markt-tabs">
    ${categories.map((cat) => `
      <button class="markt-tab-btn${activeCategory === cat ? ' is-active' : ''}" data-markt-cat="${cat}">
        ${categoryLabel(cat)}
      </button>
    `).join('')}
  </div>`;
}

// ─── Render: Item-Karte ───────────────────────────────────────────────────────

function renderItemCard(item: CatalogItem): string {
  const isContact    = item.category === 'CONTACT';
  const atLimit      = isContact
    ? (item.alreadyPurchased === true)
    : (item.remainingInPeriod !== null && item.remainingInPeriod <= 0);
  const isBlackMoney = item.currency === 'BLACK_MONEY';
  const vehicleBadge = item.vehicleCapacity !== undefined
    ? `<span class="markt-item-badge markt-item-badge--vehicle">${String(item.vehicleCapacity)} SLOTS</span>`
    : '';

  return `
    <div class="markt-item${atLimit ? ' markt-item--depleted' : ''}">
      <div class="markt-item-header">
        <span class="markt-item-label">${esc(item.label.toUpperCase())}</span>
        ${vehicleBadge}
        ${isBlackMoney ? '<span class="markt-item-badge markt-item-badge--bm">S-\u20a6</span>' : ''}
        ${item.priceFixed ? '' : priceMultiplierBadge(item.dailyMultiplier)}
      </div>
      <p class="markt-item-desc">${esc(item.description)}</p>
      ${renderLore(item, 'markt-item-lore')}

      ${!isContact && item.dailyLimit !== null ? `
        <div class="markt-item-limit">
          ${esc((item.limitLabel ?? 'LIMIT').toUpperCase())}:&nbsp;<span class="${atLimit ? 'val-red' : 'val-gold'}">${String(item.remainingInPeriod ?? 0)} / ${String(item.dailyLimit)}</span>
        </div>
      ` : ''}

      <div class="markt-supplier-list">
        ${item.suppliers.map((s) => `
          <div class="markt-supplier${s.locked ? ' markt-supplier--locked' : ''}">
            <span class="markt-supplier-name">${esc(s.label.toUpperCase())}</span>
            <span class="markt-supplier-price ${isBlackMoney ? 'val-bm' : 'val-gold'}">${fmtCents(s.priceCents, item.currency)}</span>
            <span class="markt-supplier-time">${fmtDelivery(s.deliveryMinutes)}</span>
            ${s.locked
              ? `<span class="markt-supplier-lock">${esc(s.lockReason ?? 'GESPERRT')}</span>`
              : `<button class="markt-btn markt-btn--buy" data-markt-buy="${esc(item.key)}" data-markt-supplier="${esc(s.supplier)}" ${atLimit ? 'disabled' : ''}>${atLimit && isContact ? 'BEREITS GEKAUFT' : 'KAUFEN'}</button>`
            }
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

// ─── Render: Pending Orders ───────────────────────────────────────────────────

function renderPendingOrders(): string {
  // Bestellungen in der Grace-Phase (bereits zugestellt, aber noch sichtbar) nicht doppelt
  // rendern — sie kommen ausschließlich aus deliveredGrace, nicht mehr aus pendingOrders.
  const activeOrders = pendingOrders.filter((o) => !deliveredGrace.has(o.id));
  const graceEntries = [...deliveredGrace.values()];
  if (activeOrders.length === 0 && graceEntries.length === 0) return '';
  return `
    <div class="markt-pending">
      <h3 class="markt-section-title">AUSSTEHENDE LIEFERUNGEN</h3>
      ${activeOrders.map((o) => `
        <div class="markt-order">
          <span class="markt-order-label">${esc(o.itemLabel.toUpperCase())}&nbsp;×${String(o.quantity)}</span>
          <span class="markt-order-time" data-order-time="${esc(o.id)}">${fmtCountdown(o.remainingMs)}</span>
        </div>
      `).join('')}
      ${graceEntries.map(({ order }) => `
        <div class="markt-order markt-order--delivered" style="--markt-order-fade-duration:${String(DELIVERED_DISPLAY_DURATION_MS)}ms">
          <span class="markt-order-label">${esc(order.itemLabel.toUpperCase())}&nbsp;×${String(order.quantity)}</span>
          <span class="markt-order-time markt-order-time--delivered">AUSGELIEFERT</span>
        </div>
      `).join('')}
    </div>
  `;
}

// ─── Render: Stellplatz-Ziel (NT-VIERTEL-FIX-01, V1) ─────────────────────────────

/** Grund, warum gerade kein Fahrzeug mit Stellplatz gekauft werden kann — null = Kauf möglich. */
function stellplatzBlockReason(qty: number): string | null {
  if (!stellplaetze) return 'Stellplätze werden geladen …';
  const withSlots = stellplaetze.properties;
  if (withSlots.length === 0) return 'Keine Immobilie mit Stellplatz. Versteck mit Garage kaufen oder im Lager einen Fahrzeugslot kaufen.';
  if (stellplaetze.purchasableCount < qty) {
    const homeless = stellplaetze.homeless.length;
    return homeless > 0
      ? `Alle Stellplätze vergeben — ${String(homeless)} Fahrzeug(e) stehen ohne Stellplatz. Erst unterbringen oder ausbauen.`
      : `Nicht genug Stellplätze: ${String(stellplaetze.vehiclesRequiringSlots)} Fahrzeuge, ${String(stellplaetze.totalSlots)} Plätze.`;
  }
  const target = withSlots.find((p) => p.propertyId === buyTargetPropertyId);
  if (target && target.freeCount < qty) return `STELLPLATZ BELEGT: ${describeOccupants(target)}`;
  return null;
}

function renderStellplatzTarget(): string {
  const props = stellplaetze?.properties ?? [];
  const options = renderPropertyOptions(
    props.map((p) => ({
      id: p.propertyId, subType: p.subType, customName: p.customName, nickname: p.nickname,
      isHideout: p.type === 'HIDEOUT',
    })),
    {
      selectedId: buyTargetPropertyId,
      placeholder: '— Stellplatz wählen —',
      suffix: (item) => {
        const free = props.find((p) => p.propertyId === item.id)?.freeCount ?? 0;
        return free > 0 ? `(${String(free)} Stellplatz frei)` : '(belegt)';
      },
      isDisabled: (item) => (props.find((p) => p.propertyId === item.id)?.freeCount ?? 0) === 0,
    },
  );
  const reason = stellplatzBlockReason(buyQty);
  return `
    <div class="markt-modal-row">
      <label class="markt-modal-label">STELLPLATZ</label>
      ${props.length > 0 ? `<select class="markt-select" id="markt-target-prop">${options}</select>` : ''}
      ${stellplaetze ? `<p class="markt-qty-hint">BELEGT ${String(stellplaetze.vehiclesRequiringSlots)} / ${String(stellplaetze.totalSlots)} STELLPLÄTZE</p>` : ''}
      <p class="markt-modal-warn" id="markt-stellplatz-warn"${reason ? '' : ' hidden'}>${esc(reason ?? '')}</p>
    </div>`;
}

// ─── Render: Buy-Modal ────────────────────────────────────────────────────────

function renderBuyModal(): string {
  if (!buyModal) return '';
  const { item, supplier } = buyModal;
  const pricePerUnit = parseInt(supplier.priceCents, 10);
  const totalPrice   = pricePerUnit * buyQty;
  const isBlackMoney = item.currency === 'BLACK_MONEY';
  const player       = gameState.getPlayer();
  const balance      = player ? parseInt(isBlackMoney ? player.blackMoney : player.noxBalance, 10) : 0;
  const canAfford    = balance >= totalPrice;
  // Platzhalter zuerst, ohne gültigen value — erzwingt eine aktive Lagerauswahl statt eines
  // optisch bereits "ausgewählten", aber vom Spieler nie bestätigten ersten Lagers.
  // NT-POLISH-01 (P1): zentrale Anzeige "Eigenname — Typ (X frei)", gruppiert LAGER / VERSTECKE.
  const warehouseOptions = renderPropertyOptions(
    warehouses.map((w) => ({
      id: w.id, label: w.label, customName: w.customName, nickname: w.nickname,
      isHideout: w.isHideout === true, freeSlots: w.slots - w.currentLoad,
    })),
    { selectedId: buyTargetPropertyId, placeholder: '— Lager wählen —' },
  );

  return `
    <div class="modal-backdrop" id="markt-buy-modal">
      <div class="modal modal--narrow">
        <div class="modal-header">
          <h2>${esc(item.label.toUpperCase())} KAUFEN</h2>
          <p class="modal-subtitle">${esc(supplier.label)} · ${fmtDelivery(supplier.deliveryMinutes)}</p>
        </div>
        <div class="modal-body">
          ${renderLore(item, 'markt-modal-lore')}

          <div class="markt-modal-row">
            <label class="markt-modal-label">MENGE${item.remainingInPeriod !== null ? `&nbsp;<span class="markt-qty-hint">(max heute: ${String(item.remainingInPeriod)})</span>` : ''}</label>
            <div class="markt-qty-row">
              <button class="markt-qty-btn" data-qty="-1">-</button>
              <input type="number" class="markt-qty-input" id="markt-qty-input"
                min="1"
                max="${String(item.remainingInPeriod ?? item.stackMax)}"
                value="${String(buyQty)}"
                data-markt-qty-max="${String(item.remainingInPeriod ?? item.stackMax)}" />
              <button class="markt-qty-btn" data-qty="+1">+</button>
              <button class="markt-qty-btn markt-qty-btn--max"
                data-qty-max="${String(item.remainingInPeriod ?? item.stackMax)}">MAX</button>
            </div>
          </div>

          ${item.requiresStellplatz ? renderStellplatzTarget() : item.category !== 'CONTACT' ? `
          <div class="markt-modal-row">
            <label class="markt-modal-label">ZIELLAGER</label>
            ${warehouses.length === 0
              ? '<p class="markt-modal-warn">Kein Ziellager verfügbar. Kaufe zuerst ein Versteck oder Lager.</p>'
              : `<select class="markt-select" id="markt-target-prop">${warehouseOptions}</select>`
            }
          </div>
          ` : ''}

          <div class="markt-modal-total">
            <span class="markt-modal-total-label">GESAMT</span>
            <span class="markt-modal-total-price ${isBlackMoney ? 'val-bm' : 'val-gold'}" id="markt-total-price">${fmtCents(String(totalPrice), item.currency)}</span>
          </div>

          <div class="markt-modal-balance">
            <span class="markt-modal-balance-label">KONTOSTAND</span>
            <span class="${canAfford ? (isBlackMoney ? 'val-bm' : 'val-gold') : 'val-red'}">${fmtCents(String(balance), item.currency)}</span>
          </div>

          ${!canAfford ? '<p class="markt-modal-warn">Nicht genug Guthaben.</p>' : ''}
        </div>
        <div class="modal-actions">
          <button class="btn btn--primary" id="markt-confirm-buy" ${isConfirmBuyDisabled(item, canAfford) ? 'disabled' : ''}>BESTELLEN</button>
          <button class="btn" data-close-modal>ABBRECHEN</button>
        </div>
      </div>
    </div>
  `;
}

// DOM-id für eine Subgroup-Überschrift — Ziel des Sprungmarken-Klicks (subgroupAnchorId)
// und der Überschrift selbst. subgroup-Werte sind feste Enum-artige Strings (nur
// Großbuchstaben/Unterstriche, aus market.definitions.ts), daher ohne Escaping als
// id-Suffix sicher.
function subgroupAnchorId(subgroup: string): string {
  return `markt-subgroup-${subgroup}`;
}

// NT-MARKT-TAXONOMIE-01: rendert die Item-Liste einer Kategorie, mit Subgroup-Headern
// vor dem ersten Item einer neuen subgroup (z.B. „ROLLTISCH-ZUBEHÖR" innerhalb ZUTATEN &
// VERBRAUCH). Backend liefert die Items bereits in der korrekten Anzeigereihenfolge
// (sortMarketItemsForDisplay, market.definitions.ts) — hier wird nur noch gruppiert.
function renderItemsList(items: CatalogItem[]): string {
  let html = '';
  let lastSubgroup: string | undefined;
  for (const item of items) {
    if (item.subgroup !== undefined && item.subgroup !== lastSubgroup) {
      html += `<div class="markt-subgroup-heading" id="${subgroupAnchorId(item.subgroup)}">${esc(subgroupLabel(item.subgroup))}</div>`;
    }
    lastSubgroup = item.subgroup;
    html += renderItemCard(item);
  }
  return html;
}

// NT-MARKT-SPRUNGMARKEN-01: Sprungmarken-Leiste — eine Chip-Reihe pro subgroup, die in der
// aktiven Kategorie tatsächlich vorkommt (Reihenfolge = Anzeigereihenfolge, kommt bereits
// sortiert vom Backend). Klick scrollt zur Überschrift (siehe attachListeners), kein Filtern/
// Zuklappen — die Liste bleibt vollständig sichtbar. Bei 0 oder 1 subgroup keine Leiste
// (LAB_EQUIPMENT/KONTAKTE haben absichtlich keine subgroups, siehe categoryLabel).
function renderSubgroupJumpBar(items: CatalogItem[]): string {
  const seen = new Set<string>();
  const subgroups: string[] = [];
  for (const item of items) {
    if (item.subgroup !== undefined && !seen.has(item.subgroup)) {
      seen.add(item.subgroup);
      subgroups.push(item.subgroup);
    }
  }
  if (subgroups.length < 2) return '';
  return `<div class="markt-jumpbar">
    ${subgroups.map((sg) => `<button class="markt-jump-btn" data-markt-jump="${esc(sg)}">${esc(subgroupLabel(sg))}</button>`).join('')}
  </div>`;
}

// ─── Render: Vollständige Seite ───────────────────────────────────────────────

function renderPage(): string {
  const filteredItems = catalog?.items.filter((i) => i.category === activeCategory) ?? [];

  return `
    <section class="dash-content" style="padding:0;" id="markt-root">
      <div class="markt-header">
        <div>
          <h2 class="markt-title">MARKT</h2>
          <p class="markt-subtitle">Preise variieren täglich. Fahrzeuge = Transport + Verkauf.</p>
        </div>
      </div>

      ${renderCategoryTabs()}

      ${renderSubgroupJumpBar(filteredItems)}

      ${renderPendingOrders()}

      <div class="markt-items" id="markt-item-list">
        ${catalog === null
          ? '<p class="markt-empty">LADE...</p>'
          : filteredItems.length === 0
            ? '<p class="markt-empty">Keine Produkte in dieser Kategorie.</p>'
            : renderItemsList(filteredItems)}
      </div>

      ${renderBuyModal()}
    </section>
  `;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function addStyles(): void {
  if (document.getElementById('markt-styles')) return;
  const s = document.createElement('style');
  s.id = 'markt-styles';
  s.textContent = `
    .markt-header {
      padding: var(--space-3) var(--space-4) var(--space-2);
      border-bottom: 1px solid var(--border);
    }
    .markt-title {
      font-family: var(--font-mono); font-size: 14px; letter-spacing: 0.14em;
      color: var(--accent-gold); margin: 0 0 2px; text-transform: uppercase;
    }
    @media(min-width:1024px) { .markt-title { font-size: var(--font-size-xl); } }
    .markt-subtitle { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); margin: 0; line-height: 1.4; }

    /* ── Kategorie-Tabs ── */
    .markt-tabs { display: flex; gap: var(--space-2); flex-wrap: wrap; padding: var(--space-3) var(--space-4); border-bottom: 1px solid var(--border); }
    .markt-tab-btn {
      font-family: var(--font-mono); font-size: var(--font-size-xs); letter-spacing: 0.08em; text-transform: uppercase;
      padding: 4px 10px; background: transparent; border: 1px solid var(--border);
      border-radius: var(--radius-btn); color: var(--text-muted); cursor: pointer;
      flex-shrink: 0; white-space: nowrap;
      touch-action: manipulation; -webkit-tap-highlight-color: transparent;
    }
    .markt-tab-btn.is-active { color: var(--accent-gold); border-color: var(--accent-gold); }
    .markt-tab-btn:hover:not(.is-active) { color: var(--text-primary); border-color: var(--text-muted); }
    @media ${MOBILE_LAYOUT_QUERY} {
      .markt-tabs {
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        padding-right: var(--space-8);
      }
      .markt-tabs::-webkit-scrollbar { display: none; }
    }

    /* ── Sprungmarken-Leiste (NT-MARKT-SPRUNGMARKEN-01) — eine Ebene unter den Kategorie-
       Tabs, springt per Klick zur Subgroup-Überschrift. Bewusst kleiner/unauffälliger als
       .markt-tab-btn, damit die Hierarchie (Kategorie > Subgroup) sichtbar bleibt. ── */
    .markt-jumpbar { display: flex; gap: var(--space-2); flex-wrap: wrap; padding: var(--space-3) var(--space-4); }
    .markt-jump-btn {
      font-family: var(--font-mono); font-size: var(--font-size-xs); letter-spacing: 0.05em; text-transform: uppercase;
      padding: 4px 10px; background: transparent; border: 1px solid var(--border);
      border-radius: var(--radius-btn); color: var(--text-muted); cursor: pointer;
      flex-shrink: 0; white-space: nowrap;
      touch-action: manipulation; -webkit-tap-highlight-color: transparent;
    }
    .markt-jump-btn:hover { color: var(--accent-gold); border-color: var(--accent-gold); }
    @media ${MOBILE_LAYOUT_QUERY} {
      .markt-jumpbar {
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        padding-right: var(--space-8);
      }
      .markt-jumpbar::-webkit-scrollbar { display: none; }
    }

    /* ── Pending Orders ── */
    .markt-pending { padding: var(--space-3) var(--space-4); border-bottom: 1px solid var(--border); background: var(--bg-surface); }
    .markt-section-title { font-family: var(--font-mono); font-size: var(--font-size-xs); letter-spacing: 0.10em; color: var(--text-muted); margin: 0 0 var(--space-2); }
    .markt-order { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
    .markt-order-label { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-primary); }
    .markt-order-time  { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--accent-green); }

    /* NT-UI-MODAL-LISTE-01: "AUSGELIEFERT" kurz anzeigen, dann automatisch ausfaden.
       --markt-order-fade-duration wird inline pro Eintrag aus DELIVERED_DISPLAY_DURATION_MS gesetzt. */
    .markt-order-time--delivered { color: var(--accent-gold); }
    .markt-order--delivered {
      animation-name: markt-order-fade;
      animation-duration: var(--markt-order-fade-duration, 3500ms);
      animation-timing-function: ease-out;
      animation-fill-mode: forwards;
    }
    @keyframes markt-order-fade {
      0%   { opacity: 1; }
      80%  { opacity: 1; }
      100% { opacity: 0; }
    }

    /* ── Items ── */
    .markt-items { padding: var(--space-4); display: flex; flex-direction: column; gap: var(--space-3); }
    .markt-empty { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); }

    /* NT-MARKT-TAXONOMIE-01: Subgroup-Header innerhalb einer Kategorie (z.B. Rolltisch-Zubehör
       innerhalb ZUTATEN & VERBRAUCH). */
    .markt-subgroup-heading {
      font-family: var(--font-mono); font-size: var(--font-size-xs); letter-spacing: 0.10em;
      text-transform: uppercase; color: var(--text-muted);
      padding-top: var(--space-2); margin-top: var(--space-1);
      border-top: 1px solid var(--border);
    }
    .markt-items > .markt-subgroup-heading:first-child { border-top: none; padding-top: 0; margin-top: 0; }

    .markt-item { border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-4); background: var(--bg-elevated); }
    .markt-item--depleted { opacity: 0.5; }
    .markt-item-header { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; margin-bottom: var(--space-1); }
    .markt-item-label { font-family: var(--font-mono); font-size: var(--font-size-sm); color: var(--text-primary); letter-spacing: 0.05em; }
    .markt-item-badge {
      font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase;
      padding: 2px 5px; border-radius: 2px; border: 1px solid var(--border);
    }
    .markt-item-badge--vehicle { color: var(--accent-blue);   border-color: color-mix(in srgb, var(--accent-blue)   40%, transparent); }
    .markt-item-badge--bm      { color: var(--accent-bm);     border-color: color-mix(in srgb, var(--accent-bm)     40%, transparent); }
    .markt-item-desc { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); margin: 0 0 4px; line-height: 1.4; }
    .markt-item-lore { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-italic); font-style: italic; margin: 0 0 var(--space-2); line-height: 1.4; }
    .markt-item-limit { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); margin-bottom: var(--space-2); }

    .markt-mult { font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.06em; padding: 2px 5px; border-radius: 2px; }
    .markt-mult--up      { color: var(--accent-red);   background: color-mix(in srgb, var(--accent-red)   15%, transparent); }
    .markt-mult--down    { color: var(--accent-green); background: color-mix(in srgb, var(--accent-green) 15%, transparent); }
    .markt-mult--neutral { color: var(--text-muted); }

    /* ── Supplier-Liste ── */
    .markt-supplier-list { display: flex; flex-direction: column; gap: 4px; }
    .markt-supplier {
      display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap;
      padding: var(--space-1) var(--space-2); background: var(--bg-surface); border-radius: 2px;
    }
    .markt-supplier--locked { opacity: 0.5; }
    .markt-supplier-name  { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); flex: 0 0 140px; letter-spacing: 0.04em; }
    .markt-supplier-price { font-family: var(--font-mono); font-size: var(--font-size-xs); flex: 0 0 100px; }
    .markt-supplier-time  { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); flex: 0 0 70px; }
    .markt-supplier-lock  { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted); font-style: italic; }

    .markt-btn { font-family: var(--font-mono); font-size: var(--font-size-xs); letter-spacing: 0.06em; text-transform: uppercase; padding: var(--space-1) var(--space-3); background: transparent; border: 1px solid var(--accent-green); color: var(--accent-green); border-radius: var(--radius-btn); cursor: pointer; touch-action: manipulation; min-height: 36px; }
    .markt-btn:disabled { opacity: 0.38; cursor: not-allowed; }

    /* ── Buy-Modal ── */
    .markt-modal-lore { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-italic); font-style: italic; margin: 0 0 var(--space-3); line-height: 1.5; }
    .markt-modal-row { margin-bottom: var(--space-3); }
    .markt-modal-label { display: block; font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); letter-spacing: 0.08em; margin-bottom: 8px; }
    .markt-qty-row { display: flex; align-items: center; gap: var(--space-1); }
    .markt-qty-btn { font-family: var(--font-mono); font-size: var(--font-size-sm); height: 36px; min-width: 36px; border: 1px solid var(--border); background: var(--bg-surface); color: var(--text-primary); border-radius: var(--radius-btn); cursor: pointer; padding: 0 var(--space-1); touch-action: manipulation; }
    .markt-qty-btn:hover { border-color: var(--text-muted); }
    .markt-qty-btn--max { border-color: var(--accent-gold); color: var(--accent-gold); padding: 0 var(--space-2); min-width: auto; }
    .markt-qty-btn--max:hover { background: color-mix(in srgb, var(--accent-gold) 12%, transparent); }
    .markt-qty-input { font-family: var(--font-mono); font-size: var(--font-size-sm); height: 36px; width: 64px; text-align: center; background: var(--bg-surface); border: 1px solid var(--border); color: var(--accent-gold); border-radius: var(--radius-btn); padding: 0 var(--space-1); -moz-appearance: textfield; }
    .markt-qty-input::-webkit-outer-spin-button, .markt-qty-input::-webkit-inner-spin-button { -webkit-appearance: none; }
    .markt-qty-hint { font-size: 10px; color: var(--text-muted); }
    .markt-select { font-family: var(--font-mono); font-size: var(--font-size-xs); background: var(--bg-surface); border: 1px solid var(--border); color: var(--text-primary); padding: var(--space-2) var(--space-3); border-radius: var(--radius-btn); width: 100%; }
    .markt-modal-total, .markt-modal-balance {
      display: flex; justify-content: space-between; align-items: center;
      padding: var(--space-2) 0; border-top: 1px solid var(--border);
      font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--text-muted);
    }
    .markt-modal-total-label, .markt-modal-balance-label { font-size: 11px; letter-spacing: 0.08em; }
    .markt-modal-total-price { font-size: var(--font-size-sm); }
    .markt-modal-warn { font-family: var(--font-mono); font-size: var(--font-size-xs); color: var(--accent-red); margin: var(--space-1) 0 0; }
  `;
  document.head.appendChild(s);
}

// ─── Event-Handling ───────────────────────────────────────────────────────────

function scrollActiveTabIntoView(): void {
  const activeBtn = document.querySelector<HTMLButtonElement>('.markt-tab-btn.is-active');
  if (activeBtn) activeBtn.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
}

function rerender(): void {
  const root = document.getElementById('markt-root');
  if (!root) return;
  const dashMain = document.querySelector<HTMLElement>('.dash-main');
  const savedScroll = dashMain?.scrollTop ?? 0;
  const tmp = document.createElement('div');
  tmp.innerHTML = renderPage();
  const newSection = tmp.firstElementChild;
  if (newSection) root.replaceWith(newSection);
  attachListeners();
  scrollActiveTabIntoView();
  if (dashMain) dashMain.scrollTop = savedScroll;
}

// Geteilte Bedingung für den BESTELLEN-Button — Render (renderBuyModal) und Laufzeit-Patch
// (updateQtyDisplay) müssen hier zwingend dieselbe Logik verwenden, sonst driftet der
// Button-Zustand nach einer Lager-/Mengenänderung vom tatsächlich gerenderten Zustand ab.
function isConfirmBuyDisabled(item: CatalogItem, canAfford: boolean): boolean {
  // NT-VIERTEL-FIX-01 (V1): Fahrzeuge mit Stellplatz-Pflicht — vor dem Kauf prüfen, kein Geldabzug.
  if (item.requiresStellplatz) return !canAfford || !buyTargetPropertyId || stellplatzBlockReason(buyQty) !== null;
  return !canAfford || (item.category !== 'CONTACT' && (warehouses.length === 0 || !buyTargetPropertyId));
}

function updateQtyDisplay(): void {
  const input        = document.getElementById('markt-qty-input')       as HTMLInputElement | null;
  const totalDisplay = document.getElementById('markt-total-price');
  const confirmBtn   = document.getElementById('markt-confirm-buy')     as HTMLButtonElement | null;
  if (input) input.value = String(buyQty);
  if (buyModal && totalDisplay) {
    const total = parseInt(buyModal.supplier.priceCents, 10) * buyQty;
    totalDisplay.textContent = fmtCents(String(total), buyModal.item.currency);
  }
  if (confirmBtn && buyModal) {
    const player    = gameState.getPlayer();
    const isBm      = buyModal.item.currency === 'BLACK_MONEY';
    const balance   = player ? parseInt(isBm ? player.blackMoney : player.noxBalance, 10) : 0;
    const total     = parseInt(buyModal.supplier.priceCents, 10) * buyQty;
    const canAfford = balance >= total;
    confirmBtn.disabled = isConfirmBuyDisabled(buyModal.item, canAfford);
  }
  // NT-VIERTEL-FIX-01 (V1): Stellplatz-Hinweis folgt Menge/Ziel ohne Voll-Render.
  const warn = document.getElementById('markt-stellplatz-warn');
  if (warn && buyModal?.item.requiresStellplatz) {
    const reason = stellplatzBlockReason(buyQty);
    warn.textContent = reason ?? '';
    warn.hidden = reason === null;
  }
}

function attachListeners(): void {
  const root = document.getElementById('markt-root');
  if (!root) return;

  root.addEventListener('click', (e) => {
    const target = e.target as Element;

    // Kategorie wechseln
    const catBtn = target.closest<HTMLButtonElement>('[data-markt-cat]');
    if (catBtn) {
      activeCategory = catBtn.dataset.marktCat as CatalogItem['category'];
      buyModal = null;
      rerender();
      return;
    }

    // NT-MARKT-SPRUNGMARKEN-01: Sprungmarke → zur Subgroup-Überschrift scrollen (kein Filtern,
    // kein Zuklappen — die Liste bleibt komplett sichtbar, nur die Scroll-Position ändert sich).
    const jumpBtn = target.closest<HTMLButtonElement>('[data-markt-jump]');
    if (jumpBtn) {
      const heading = document.getElementById(subgroupAnchorId(jumpBtn.dataset.marktJump ?? ''));
      heading?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Kaufen-Button → Modal öffnen
    const buyBtn = target.closest<HTMLButtonElement>('[data-markt-buy]');
    if (buyBtn && !buyBtn.disabled) {
      const itemKey  = buyBtn.dataset.marktBuy ?? '';
      const supplier = buyBtn.dataset.marktSupplier ?? '';
      const item     = catalog?.items.find((i) => i.key === itemKey);
      const sup      = item?.suppliers.find((s) => s.supplier === supplier);
      if (item && sup) {
        buyModal = { item, supplier: sup };
        buyQty   = 1;
        // Bewusst KEIN vorausgewähltes Lager (Platzhalter erzwingt eine aktive Entscheidung) —
        // ein stillschweigend vorausgewähltes erstes Lager führte zu Fehlkäufen ins falsche
        // Lager, wenn direkt auf BESTELLEN geklickt wurde.
        buyTargetPropertyId = '';
        rerender();
      }
      return;
    }

    // Modal: Modal schließen
    if (target.closest('[data-close-modal]') || target.id === 'markt-buy-modal') {
      buyModal = null;
      rerender();
      return;
    }

    // Modal: +/- Buttons
    const qtyBtn = target.closest<HTMLButtonElement>('[data-qty]');
    if (qtyBtn) {
      const delta  = parseInt(qtyBtn.dataset.qty ?? '0', 10);
      const input  = document.getElementById('markt-qty-input') as HTMLInputElement | null;
      const maxQty = input ? parseInt(input.dataset.marktQtyMax ?? '99', 10) : 99;
      buyQty = Math.max(1, Math.min(maxQty, buyQty + delta));
      updateQtyDisplay();
      return;
    }

    // Modal: MAX-Button
    const maxBtn = target.closest<HTMLButtonElement>('[data-qty-max]');
    if (maxBtn) {
      const maxQty = parseInt(maxBtn.dataset.qtyMax ?? '1', 10);
      buyQty = Math.max(1, maxQty);
      updateQtyDisplay();
      return;
    }

    // Modal: Kauf bestätigen
    if (target.id === 'markt-confirm-buy') {
      const targetEl    = document.getElementById('markt-target-prop') as HTMLSelectElement | null;
      const propId      = targetEl?.value ?? buyTargetPropertyId;
      const isContactBuy = buyModal?.item.category === 'CONTACT';
      if (buyModal && (propId || isContactBuy)) {
        void handleBuy(buyModal.item.key, buyQty, buyModal.supplier.supplier, propId);
      }
      return;
    }
  });

  root.addEventListener('change', (e) => {
    const t = e.target as HTMLElement;

    // Ziellager-Auswahl — Button-Zustand muss sofort neu bewertet werden (vorher blieb
    // BESTELLEN nach der ersten echten Auswahl stumm deaktiviert, da nichts den Button erneut
    // prüfte).
    const sel = t.closest<HTMLSelectElement>('#markt-target-prop');
    if (sel) { buyTargetPropertyId = sel.value; updateQtyDisplay(); return; }

    // Direkte Mengen-Eingabe im Input-Feld
    const qtyInput = t.closest<HTMLInputElement>('#markt-qty-input');
    if (qtyInput && buyModal) {
      const maxQty = parseInt(qtyInput.dataset.marktQtyMax ?? '99', 10);
      const raw    = parseInt(qtyInput.value, 10);
      buyQty = Math.max(1, Math.min(maxQty, isNaN(raw) ? 1 : raw));
      updateQtyDisplay();
    }
  });
}

async function handleBuy(itemKey: string, quantity: number, supplier: string, targetPropertyId: string): Promise<void> {
  const confirmBtn = document.getElementById('markt-confirm-buy') as HTMLButtonElement | null;
  if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.textContent = '...'; }

  try {
    await api.post<unknown>('/market/buy', { itemKey, quantity, supplier, targetPropertyId });
    buyModal = null;
    showToast('Bestellung aufgegeben!', 'info');
    void refreshBalance();
    await reload();
  } catch (err) {
    if (tryOfferEmergencyGarage(err, () => { void reload(); })) { rerender(); return; }
    showToast(err instanceof ApiError ? err.message : 'Kauf fehlgeschlagen.', 'error');
    rerender();
  }
}

// ─── Countdown in-place ───────────────────────────────────────────────────────

function updateCountdowns(): void {
  const now = Date.now();
  let needsRerender = false;

  for (const o of pendingOrders) {
    if (deliveredGrace.has(o.id)) continue; // wird unten über die Grace-Map behandelt
    const remaining = Math.max(0, new Date(o.deliveredAt).getTime() - now);
    if (remaining <= 0) {
      deliveredGrace.set(o.id, { order: o, graceStartedAt: now });
      needsRerender = true;
      continue;
    }
    const el = document.querySelector<HTMLElement>(`[data-order-time="${o.id}"]`);
    if (el) el.textContent = fmtCountdown(remaining);
  }

  // Grace-Phase-Ablauf: Eintrag endgültig entfernen, sobald DELIVERED_DISPLAY_DURATION_MS um ist.
  for (const [id, entry] of deliveredGrace) {
    if (now - entry.graceStartedAt >= DELIVERED_DISPLAY_DURATION_MS) {
      deliveredGrace.delete(id);
      needsRerender = true;
    }
  }

  if (needsRerender) rerender();
}

// ─── Daten laden ─────────────────────────────────────────────────────────────

/** @param background true = Aufruf aus dem Polling-Timer (Render-Pause bei offener Auswahl, H7). */
async function reload(background = false): Promise<void> {
  const [catalogResult, ordersResult, propsResult, stellplatzResult] = await Promise.allSettled([
    api.get<CatalogResponse>('/market/catalog'),
    api.get<{ orders: PendingOrder[] }>('/market/orders'),
    api.get<{ warehouses: OwnedWarehouse[] }>('/properties'),
    fetchStellplaetze(),
  ]);

  if (stellplatzResult.status === 'fulfilled') { stellplaetze = stellplatzResult.value; }
  else { console.error('[Markt] /vehicle/stellplaetze Fehler:', stellplatzResult.reason); }

  if (catalogResult.status === 'fulfilled')  { catalog       = catalogResult.value; }
  else { console.error('[Markt] /market/catalog Fehler:', catalogResult.reason); }

  if (ordersResult.status === 'fulfilled')   { pendingOrders = ordersResult.value.orders; }
  else { console.error('[Markt] /market/orders Fehler:', ordersResult.reason); }

  if (propsResult.status === 'fulfilled') {
    const r = propsResult.value as PropertiesResponse;
    const hw: OwnedWarehouse[] = r.hideouts.map((h) => ({ ...h, isHideout: true }));
    warehouses = [...r.warehouses, ...hw];
  } else { console.error('[Markt] /properties Fehler:', propsResult.reason); }

  // NT-HOTFIX-01 (H7): Polling-Refresh nicht über ein offenes Dropdown/Kauf-Modal hinweg rendern.
  if (background) renderWhenIdle(MARKT_RENDER_KEY, 'markt-root', rerender);
  else rerender();
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

export function renderMarktTab(): string {
  return `
    <section class="dash-content" style="padding:0;" id="markt-root">
      <p style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);text-align:center;padding:var(--space-8) 0;letter-spacing:0.08em;">LADE...</p>
    </section>
  `;
}

export function mountMarktTab(): void {
  addStyles();
  void reload();
  refreshTimer   = setInterval(() => { void reload(true); }, 60_000);
  countdownTimer = setInterval(updateCountdowns, 1_000);
}

export function cleanupMarkt(): void {
  if (refreshTimer   !== null) { clearInterval(refreshTimer);   refreshTimer   = null; }
  if (countdownTimer !== null) { clearInterval(countdownTimer); countdownTimer = null; }
  cancelPendingRender(MARKT_RENDER_KEY);
  catalog       = null;
  pendingOrders = [];
  buyModal      = null;
  deliveredGrace.clear();
}
